import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AccessGate from '../components/pejman/AccessGate';
import InstallBanner from '../components/pejman/InstallBanner';
import usePwaInstall from '../hooks/usePwaInstall';
import {
  ACCESS_STORAGE_KEY,
  COMPANY,
  DRAFT_KEY,
  LAST_INVOICE_KEY,
  RECENT_CLIENTS_KEY,
  SERVICE_TEMPLATES,
  buildSavePayload,
  buildTextSummary,
  buildVenmoAppUrl,
  buildVenmoPaymentMessage,
  buildVenmoPaymentUrl,
  calculateTotals,
  createEmptyForm,
  discountLineLabel,
  generateFormalPDF,
  money,
  INVOICE_CSS
} from '../utils/pejmanInvoice';

const PejmanPage = () => {
  const [searchParams] = useSearchParams();
  const { isStandalone } = usePwaInstall();
  const [unlocked, setUnlocked] = useState(
    () => sessionStorage.getItem(ACCESS_STORAGE_KEY) === '1'
  );
  const [formData, setFormData] = useState(() =>
    createEmptyForm(searchParams.get('type') === 'estimate')
  );
  const [openSection, setOpenSection] = useState('customer');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [hasDraft, setHasDraft] = useState(false);
  const [customTemplates, setCustomTemplates] = useState({});
  const [recentClients, setRecentClients] = useState([]);
  const [dbCustomers, setDbCustomers] = useState([]);
  const [showCustomerSuggestions, setShowCustomerSuggestions] = useState(false);
  const [customerSearchTimeout, setCustomerSearchTimeout] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [convertEstNum, setConvertEstNum] = useState('');
  const [shareStatus, setShareStatus] = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  const [editTerms, setEditTerms] = useState(false);

  const discLabel = useMemo(() => discountLineLabel(formData), [formData]);

  // Recalc totals when line items / tax / discount change
  useEffect(() => {
    setFormData((prev) => {
      const totals = calculateTotals(prev);
      if (
        prev.subtotal === totals.subtotal &&
        prev.taxAmount === totals.taxAmount &&
        prev.appliedDiscount === totals.appliedDiscount &&
        prev.totalAmount === totals.totalAmount
      ) {
        return prev;
      }
      return { ...prev, ...totals };
    });
  }, [
    formData.lineItems,
    formData.taxRate,
    formData.discount,
    formData.discountPercent,
    formData.discountMode
  ]);

  // Auto-save draft
  useEffect(() => {
    if (!unlocked) return undefined;
    const timer = setTimeout(() => {
      if (
        formData.clientName ||
        formData.clientAddress ||
        formData.lineItems[0]?.description ||
        formData.poNumber
      ) {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(formData));
        setHasDraft(true);
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [formData, unlocked]);

  // Load templates / recent
  useEffect(() => {
    if (!unlocked) return;
    const draft = localStorage.getItem(DRAFT_KEY);
    if (draft) setHasDraft(true);

    const clients = localStorage.getItem(RECENT_CLIENTS_KEY);
    if (clients) {
      try {
        setRecentClients(JSON.parse(clients));
      } catch (_) {
        /* ignore */
      }
    }

    (async () => {
      try {
        const response = await fetch(`${window.location.origin}/api/public/service-templates`);
        if (!response.ok) return;
        const data = await response.json();
        if (data.status === 'success' && data.data?.templates) {
          const templatesObj = {};
          data.data.templates.forEach((template) => {
            if (!template.isDefault) {
              templatesObj[`db-${template.id}`] = {
                id: template.id,
                name: template.name,
                items: template.items,
                labor: template.labor
              };
            }
          });
          setCustomTemplates(templatesObj);
        }
      } catch (_) {
        /* ignore */
      }
    })();
  }, [unlocked]);

  const toggleSection = (id) => {
    setOpenSection((prev) => (prev === id ? '' : id));
  };

  const setDocType = (isEstimate) => {
    setFormData((prev) => ({
      ...prev,
      isEstimate,
      invoiceNumber: isEstimate
        ? prev.invoiceNumber.startsWith('EST-')
          ? prev.invoiceNumber
          : `EST-${Date.now()}`
        : prev.invoiceNumber.startsWith('INV-')
          ? prev.invoiceNumber
          : `INV-${Date.now()}`
    }));
  };

  const searchCustomers = async (query) => {
    if (!query || query.trim().length < 2) {
      setDbCustomers([]);
      return;
    }
    try {
      const res = await fetch(
        `/api/public/customers/search?q=${encodeURIComponent(query)}&limit=8`
      );
      if (res.ok) {
        const data = await res.json();
        setDbCustomers(data.data?.customers || []);
      }
    } catch (_) {
      /* ignore */
    }
  };

  const handleClientNameChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, clientName: value }));
    setShowCustomerSuggestions(true);
    if (customerSearchTimeout) clearTimeout(customerSearchTimeout);
    setCustomerSearchTimeout(setTimeout(() => searchCustomers(value), 250));
  };

  const selectCustomer = (customer) => {
    setFormData((prev) => ({
      ...prev,
      clientName: customer.name || '',
      clientEmail: customer.email || '',
      clientPhone: customer.phone || '',
      clientAddress: customer.address || '',
      clientModel: customer.acModel || ''
    }));
    setShowCustomerSuggestions(false);
    setDbCustomers([]);
    if (navigator.vibrate) navigator.vibrate(10);
  };

  const loadRecentClient = (client) => {
    setFormData((prev) => ({
      ...prev,
      clientName: client.name || '',
      clientAddress: client.address || '',
      clientPhone: client.phone || '',
      clientEmail: client.email || '',
      clientModel: client.model || ''
    }));
    setOpenSection('customer');
  };

  const saveToRecentClients = (clientData) => {
    const newClient = {
      id: Date.now(),
      name: clientData.clientName,
      address: clientData.clientAddress,
      phone: clientData.clientPhone,
      email: clientData.clientEmail,
      model: clientData.clientModel,
      lastUsed: new Date().toISOString()
    };
    const updated = [
      newClient,
      ...recentClients.filter((c) => c.name !== clientData.clientName)
    ].slice(0, 10);
    setRecentClients(updated);
    localStorage.setItem(RECENT_CLIENTS_KEY, JSON.stringify(updated));
  };

  const handleLineItemChange = (index, field, value) => {
    const updated = [...formData.lineItems];
    const item = { ...updated[index], [field]: value };
    if (field === 'qty' || field === 'price') {
      item.amount = parseFloat(item.qty || 0) * parseFloat(item.price || 0);
    }
    updated[index] = item;
    setFormData((prev) => ({ ...prev, lineItems: updated }));
  };

  const addLineItem = () => {
    setFormData((prev) => ({
      ...prev,
      lineItems: [...prev.lineItems, { qty: 1, description: '', price: 0, amount: 0 }]
    }));
  };

  const removeLineItem = (index) => {
    if (formData.lineItems.length <= 1) return;
    const updated = [...formData.lineItems];
    updated.splice(index, 1);
    setFormData((prev) => ({ ...prev, lineItems: updated }));
  };

  const loadServiceTemplate = (key) => {
    if (!key) return;
    const all = { ...SERVICE_TEMPLATES, ...customTemplates };
    const template = all[key];
    if (!template) return;
    setFormData((prev) => ({
      ...prev,
      lineItems: template.items,
      laborPerformed: template.labor
    }));
    setOpenSection('items');
  };

  const loadDraft = () => {
    const draft = localStorage.getItem(DRAFT_KEY);
    if (!draft) return;
    try {
      setFormData(JSON.parse(draft));
      setSuccessMsg('Draft loaded');
    } catch (_) {
      setError('Failed to load draft');
    }
  };

  const clearDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    setHasDraft(false);
    setSuccessMsg('Draft cleared');
  };

  const copyLastInvoice = () => {
    const last = localStorage.getItem(LAST_INVOICE_KEY);
    if (!last) {
      setError('No previous invoice found');
      return;
    }
    try {
      const parsed = JSON.parse(last);
      setFormData({
        ...parsed,
        invoiceNumber: `INV-${Date.now()}`,
        serviceDate: new Date().toISOString().split('T')[0],
        isEstimate: false
      });
      setSuccessMsg('Last invoice copied');
      setShowMore(false);
    } catch (_) {
      setError('Failed to copy last invoice');
    }
  };

  const convertEstimateToInvoice = async () => {
    if (!convertEstNum.trim()) {
      setError('Enter estimate number');
      return;
    }
    try {
      setIsLoading(true);
      setError('');
      const response = await fetch(`/api/public/estimates/${convertEstNum.trim()}`);
      if (!response.ok) throw new Error('Estimate not found');
      const data = await response.json();
      const estimate = data.data;
      let clientInfo = {};
      try {
        clientInfo = JSON.parse(estimate.notes || '{}');
      } catch (_) {
        clientInfo = estimate.customFields || {};
      }
      setFormData({
        invoiceNumber: `INV-${Date.now()}`,
        serviceDate: new Date().toISOString().split('T')[0],
        clientName: clientInfo.clientName || '',
        clientAddress: clientInfo.clientAddress || '',
        clientPhone: clientInfo.clientPhone || '',
        clientEmail: clientInfo.clientEmail || '',
        clientModel: clientInfo.clientModel || '',
        poNumber: clientInfo.poNumber || '',
        isEstimate: false,
        lineItems: (estimate.lineItems || []).length
          ? estimate.lineItems.map((item) => ({
              qty: item.qty ?? item.quantity ?? 1,
              description: item.description || '',
              price: item.price ?? item.unitPrice ?? 0,
              amount: item.amount ?? item.total ?? 0
            }))
          : [{ qty: 1, description: '', price: 0, amount: 0 }],
        laborPerformed: clientInfo.laborPerformed || '',
        subtotal: estimate.subTotal || 0,
        taxRate: estimate.taxRate || 0.0825,
        taxAmount: estimate.taxAmount || 0,
        discountMode: clientInfo.discountMode === 'percent' ? 'percent' : 'amount',
        discount: clientInfo.discount != null ? clientInfo.discount : 0,
        discountPercent: clientInfo.discountPercent != null ? clientInfo.discountPercent : 0,
        appliedDiscount: 0,
        totalAmount: estimate.totalAmount || 0,
        notes: '',
        terms: COMPANY.defaultTerms
      });
      setSuccessMsg(`Estimate ${convertEstNum} converted`);
      setShowMore(false);
      setConvertEstNum('');
    } catch (err) {
      setError(err.message || 'Failed to load estimate');
    } finally {
      setIsLoading(false);
    }
  };

  const sendPDFEmail = async (pdf) => {
    const pdfBase64 = pdf.output('dataurlstring').split(',')[1];
    await fetch('/api/public/invoices/send-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        invoiceData: {
          ...formData,
          discount: Number(formData.appliedDiscount || 0),
          discountDisplayLabel: discLabel || 'Discount'
        },
        pdfBase64,
        invoiceNumber: formData.invoiceNumber
      })
    });
  };

  const handleSave = async () => {
    setIsLoading(true);
    setError('');
    setSuccessMsg('');
    if (!formData.clientName || !formData.clientAddress) {
      setError('Client name and address are required.');
      setOpenSection('customer');
      setIsLoading(false);
      return;
    }
    try {
      const payload = buildSavePayload(formData);
      const endpoint = formData.isEstimate
        ? '/api/public/estimates'
        : '/api/public/invoices';
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to save ${formData.isEstimate ? 'estimate' : 'invoice'}`
        );
      }
      if (!formData.isEstimate) {
        localStorage.setItem(LAST_INVOICE_KEY, JSON.stringify(formData));
      }
      saveToRecentClients(formData);
      localStorage.removeItem(DRAFT_KEY);
      setHasDraft(false);
      setSuccessMsg(
        `${formData.isEstimate ? 'Estimate' : 'Invoice'} saved successfully`
      );
      try {
        const pdf = await generateFormalPDF(formData);
        await sendPDFEmail(pdf);
      } catch (_) {
        /* email optional */
      }
      setFormData((prev) => ({
        ...prev,
        invoiceNumber: prev.isEstimate ? `EST-${Date.now()}` : `INV-${Date.now()}`
      }));
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setIsLoading(false);
    }
  };

  const downloadPDF = async () => {
    try {
      setShareLoading(true);
      const pdf = await generateFormalPDF(formData);
      pdf.save(`${formData.invoiceNumber}.pdf`);
      try {
        await sendPDFEmail(pdf);
      } catch (_) {
        /* optional */
      }
      setShareStatus('PDF downloaded');
    } catch (err) {
      setError('Failed to generate PDF');
    } finally {
      setShareLoading(false);
    }
  };

  const openVenmoPay = () => {
    if (formData.isEstimate) {
      setShareStatus('Venmo pay is for invoices — convert estimate first');
      return;
    }
    if (!(Number(formData.totalAmount) > 0)) {
      setShareStatus('Add line items before collecting payment');
      return;
    }
    const webUrl = buildVenmoPaymentUrl(formData);
    const appUrl = buildVenmoAppUrl(formData);
    const mobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || '');
    if (mobile) {
      // Native scheme opens Venmo app; HTTPS is shareable fallback
      window.location.href = appUrl;
      window.setTimeout(() => {
        window.open(webUrl, '_blank', 'noopener,noreferrer');
      }, 1200);
    } else {
      window.open(webUrl, '_blank', 'noopener,noreferrer');
    }
    setShareStatus('Opened Venmo payment');
  };

  const shareVenmoLink = async () => {
    if (formData.isEstimate) {
      setShareStatus('Venmo pay is for invoices');
      return;
    }
    if (!(Number(formData.totalAmount) > 0)) {
      setShareStatus('Add line items before sharing payment link');
      return;
    }
    const text = buildVenmoPaymentMessage(formData);
    const url = buildVenmoPaymentUrl(formData);
    try {
      setShareLoading(true);
      if (navigator.share) {
        await navigator.share({
          title: `Pay invoice ${formData.invoiceNumber}`,
          text,
          url
        });
        setShareStatus('Payment link shared');
      } else {
        await navigator.clipboard.writeText(text);
        setShareStatus('Payment link copied');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        try {
          await navigator.clipboard.writeText(text);
          setShareStatus('Payment link copied');
        } catch (_) {
          setShareStatus(err.message || 'Share failed');
        }
      }
    } finally {
      setShareLoading(false);
    }
  };

  const copyVenmoLink = async () => {
    if (formData.isEstimate) {
      setShareStatus('Venmo pay is for invoices');
      return;
    }
    try {
      await navigator.clipboard.writeText(buildVenmoPaymentMessage(formData));
      setShareStatus('Venmo payment message copied');
    } catch (_) {
      setShareStatus('Copy failed');
    }
  };

  const shareNative = async () => {
    try {
      setShareLoading(true);
      const pdf = await generateFormalPDF(formData);
      const pdfFile = new File([pdf.output('blob')], `${formData.invoiceNumber}.pdf`, {
        type: 'application/pdf'
      });
      const payLine = formData.isEstimate
        ? ''
        : `\n\nPay with Venmo:\n${buildVenmoPaymentUrl(formData)}`;
      const shareData = {
        title: `${formData.isEstimate ? 'Estimate' : 'Invoice'} ${formData.invoiceNumber}`,
        text: `${formData.isEstimate ? 'Estimate' : 'Invoice'} from ${COMPANY.name}\nTotal: ${money(formData.totalAmount)}${payLine}`,
        files: [pdfFile]
      };
      if (navigator.share && navigator.canShare?.(shareData)) {
        await navigator.share(shareData);
        setShareStatus('Shared');
      } else if (navigator.share) {
        await navigator.share({ title: shareData.title, text: shareData.text });
        setShareStatus('Shared (text)');
      } else {
        setShareStatus('Share not available — use Download or copy');
      }
    } catch (err) {
      if (err.name !== 'AbortError') setShareStatus(err.message || 'Share failed');
    } finally {
      setShareLoading(false);
    }
  };

  const sendToClient = async () => {
    if (!formData.clientEmail) {
      setShareStatus('Add client email first');
      return;
    }
    try {
      setShareLoading(true);
      const pdf = await generateFormalPDF(formData);
      const pdfBase64 = pdf.output('dataurlstring').split(',')[1];
      const response = await fetch('/api/public/invoices/send-to-client', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invoiceData: {
            ...formData,
            discount: Number(formData.appliedDiscount || 0),
            discountDisplayLabel: discLabel || 'Discount'
          },
          pdfBase64,
          invoiceNumber: formData.invoiceNumber,
          clientEmail: formData.clientEmail,
          clientName: formData.clientName
        })
      });
      if (!response.ok) throw new Error('Failed to send email');
      setShareStatus(`Sent to ${formData.clientEmail}`);
    } catch (err) {
      setShareStatus(err.message || 'Send failed');
    } finally {
      setShareLoading(false);
    }
  };

  const copySummary = async () => {
    const summary = buildTextSummary(formData);
    try {
      await navigator.clipboard.writeText(summary);
      setShareStatus('Summary copied');
    } catch (_) {
      setShareStatus('Copy failed');
    }
  };

  const startNew = () => {
    setFormData(createEmptyForm(formData.isEstimate));
    setSuccessMsg('');
    setError('');
    setOpenSection('customer');
  };

  if (!unlocked) {
    return <AccessGate onUnlock={() => setUnlocked(true)} />;
  }

  const sectionHeader = (id, title, summary) => (
    <button
      type="button"
      onClick={() => toggleSection(id)}
      className="w-full flex items-center justify-between min-h-[52px] px-4 py-3 text-left cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#012E53]"
    >
      <div>
        <div className="text-[15px] font-semibold text-slate-800">{title}</div>
        {summary ? (
          <div className="text-xs text-slate-500 mt-0.5 truncate max-w-[260px]">{summary}</div>
        ) : null}
      </div>
      <span className="text-slate-400 text-lg">{openSection === id ? '▾' : '▸'}</span>
    </button>
  );

  return (
    <div
      className={`pejman-app text-slate-900 pb-28 ${isStandalone ? 'pejman-standalone' : ''}`}
      style={{ background: INVOICE_CSS.background, color: INVOICE_CSS.foreground }}
    >
      {/* Top bar */}
      <header
        className="pejman-header sticky top-0 z-30 text-white shadow-md"
        style={{ background: INVOICE_CSS.primary }}
      >
        <div className="px-4 pb-2 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <img
              src={COMPANY.logoUi}
              alt=""
              width={120}
              height={48}
              className="h-9 w-auto max-w-[120px] object-contain bg-white rounded-md px-1.5 py-0.5 shrink-0"
              aria-hidden="true"
            />
            <div className="min-w-0">
              <div className="text-sm font-semibold tracking-wide">Pejman</div>
              <div className="text-[11px] text-white/70 truncate">Invoice &amp; Estimate</div>
            </div>
          </div>
          <div
            className="text-[10px] font-bold px-2.5 py-1 rounded-md tracking-[0.06em] uppercase shrink-0"
            style={{
              background: formData.isEstimate ? INVOICE_CSS.estimate : 'rgba(255,255,255,0.18)',
              color: '#FFFFFF'
            }}
          >
            {formData.isEstimate ? 'Estimate · 30 days' : 'Invoice'}
          </div>
        </div>
        <div className="px-4 pb-3">
          <div className="grid grid-cols-2 gap-1 p-1 bg-black/20 rounded-xl">
            <button
              type="button"
              onClick={() => setDocType(false)}
              className={`min-h-[44px] rounded-lg text-sm font-semibold transition-colors duration-200 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 ${
                !formData.isEstimate ? 'bg-white shadow-sm' : 'text-white/80'
              }`}
              style={!formData.isEstimate ? { color: INVOICE_CSS.primary } : undefined}
            >
              Invoice
            </button>
            <button
              type="button"
              onClick={() => setDocType(true)}
              className={`min-h-[44px] rounded-lg text-sm font-semibold transition-colors duration-200 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 ${
                formData.isEstimate ? 'bg-white shadow-sm' : 'text-white/80'
              }`}
              style={formData.isEstimate ? { color: INVOICE_CSS.primary } : undefined}
            >
              Estimate
            </button>
          </div>
        </div>
      </header>

      <InstallBanner />

      <main className="px-3 pt-3 space-y-2 max-w-lg mx-auto">
        {(error || successMsg) && (
          <div
            className={`rounded-xl px-4 py-3 text-sm ${
              error ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-emerald-50 text-emerald-800 border border-emerald-200'
            }`}
            role="status"
          >
            {error || successMsg}
            {successMsg ? (
              <div className="flex gap-2 mt-2">
                <button
                  type="button"
                  onClick={startNew}
                  className="min-h-[40px] px-3 rounded-lg bg-emerald-700 text-white text-xs font-semibold"
                >
                  New
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowMore(true);
                    setShareStatus('');
                  }}
                  className="min-h-[40px] px-3 rounded-lg bg-white border border-emerald-300 text-emerald-800 text-xs font-semibold"
                >
                  Share
                </button>
              </div>
            ) : null}
          </div>
        )}

        {/* Doc meta */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 grid grid-cols-2 gap-2">
          <label className="block">
            <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">
              {formData.isEstimate ? 'Estimate #' : 'Invoice #'}
            </span>
            <input
              value={formData.invoiceNumber}
              onChange={(e) =>
                setFormData((p) => ({ ...p, invoiceNumber: e.target.value }))
              }
              className="mt-1 w-full min-h-[44px] px-3 rounded-xl border border-slate-200 text-sm"
            />
          </label>
          <label className="block">
            <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">
              Service date
            </span>
            <input
              type="date"
              value={formData.serviceDate}
              onChange={(e) =>
                setFormData((p) => ({ ...p, serviceDate: e.target.value }))
              }
              className="mt-1 w-full min-h-[44px] px-3 rounded-xl border border-slate-200 text-sm"
            />
          </label>
        </div>

        {/* Quick templates */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3">
          <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wide mb-2">
            Quick templates
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {Object.entries(SERVICE_TEMPLATES).map(([key, t]) => (
              <button
                key={key}
                type="button"
                onClick={() => loadServiceTemplate(key)}
                className="shrink-0 min-h-[40px] px-3 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold border border-slate-200"
              >
                {t.name}
              </button>
            ))}
            {Object.entries(customTemplates).map(([key, t]) => (
              <button
                key={key}
                type="button"
                onClick={() => loadServiceTemplate(key)}
                className="shrink-0 min-h-[40px] px-3 rounded-full bg-indigo-50 text-indigo-700 text-xs font-semibold border border-indigo-100"
              >
                {t.name}
              </button>
            ))}
          </div>
          {hasDraft ? (
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                onClick={loadDraft}
                className="text-xs font-semibold text-[#012E53] min-h-[36px]"
              >
                Load draft
              </button>
              <button
                type="button"
                onClick={clearDraft}
                className="text-xs font-semibold text-slate-500 min-h-[36px]"
              >
                Clear draft
              </button>
            </div>
          ) : null}
        </div>

        {/* Customer */}
        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          {sectionHeader(
            'customer',
            'Customer',
            formData.clientName || 'Select or enter customer'
          )}
          {openSection === 'customer' ? (
            <div className="px-4 pb-4 space-y-3 border-t border-slate-100">
              {recentClients.length > 0 ? (
                <div className="flex gap-2 overflow-x-auto pt-3">
                  {recentClients.slice(0, 6).map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => loadRecentClient(c)}
                      className="shrink-0 min-h-[40px] px-3 rounded-xl bg-slate-50 border border-slate-200 text-xs font-medium text-slate-700"
                    >
                      {c.name}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="pt-2" />
              )}
              <label className="block relative">
                <span className="text-xs font-medium text-slate-600">Name *</span>
                <input
                  value={formData.clientName}
                  onChange={handleClientNameChange}
                  onFocus={() => setShowCustomerSuggestions(true)}
                  placeholder="Search name, email, or phone"
                  autoComplete="off"
                  className="mt-1 w-full min-h-[48px] px-3 rounded-xl border border-slate-300 text-base"
                />
                {showCustomerSuggestions && dbCustomers.length > 0 ? (
                  <ul className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-auto">
                    {dbCustomers.map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          onClick={() => selectCustomer(c)}
                          className="w-full text-left px-3 py-3 min-h-[48px] hover:bg-slate-50 border-b border-slate-100 last:border-0"
                        >
                          <div className="text-sm font-semibold text-slate-800">{c.name}</div>
                          <div className="text-xs text-slate-500">
                            {[c.phone, c.email].filter(Boolean).join(' · ')}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </label>
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Service address *</span>
                <textarea
                  value={formData.clientAddress}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, clientAddress: e.target.value }))
                  }
                  rows={2}
                  placeholder="Street, City, State, ZIP"
                  className="mt-1 w-full px-3 py-3 rounded-xl border border-slate-300 text-base"
                />
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Phone</span>
                  <input
                    type="tel"
                    inputMode="tel"
                    value={formData.clientPhone}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, clientPhone: e.target.value }))
                    }
                    placeholder="(713) 000-0000"
                    className="mt-1 w-full min-h-[48px] px-3 rounded-xl border border-slate-300 text-base"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Email</span>
                  <input
                    type="email"
                    inputMode="email"
                    value={formData.clientEmail}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, clientEmail: e.target.value }))
                    }
                    placeholder="client@email.com"
                    className="mt-1 w-full min-h-[48px] px-3 rounded-xl border border-slate-300 text-base"
                  />
                </label>
              </div>
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Equipment model</span>
                <input
                  value={formData.clientModel}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, clientModel: e.target.value }))
                  }
                  placeholder="e.g., Carrier 24ACC636A003"
                  className="mt-1 w-full min-h-[48px] px-3 rounded-xl border border-slate-300 text-base"
                />
              </label>
            </div>
          ) : null}
        </section>

        {/* Job details */}
        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          {sectionHeader(
            'job',
            'Job details',
            formData.poNumber ? `PO #${formData.poNumber}` : 'PO number, notes'
          )}
          {openSection === 'job' ? (
            <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
              <label className="block">
                <span className="text-xs font-medium text-slate-600">
                  PO number <span className="text-slate-400">(optional)</span>
                </span>
                <input
                  value={formData.poNumber}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, poNumber: e.target.value }))
                  }
                  placeholder="Customer purchase order #"
                  className="mt-1 w-full min-h-[48px] px-3 rounded-xl border border-slate-300 text-base"
                />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Notes</span>
                <textarea
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, notes: e.target.value }))
                  }
                  rows={2}
                  placeholder="Internal or customer notes"
                  className="mt-1 w-full px-3 py-3 rounded-xl border border-slate-300 text-base"
                />
              </label>
              {formData.isEstimate ? (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
                  This estimate is valid for 30 days from the service date.
                </p>
              ) : null}
            </div>
          ) : null}
        </section>

        {/* Items */}
        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          {sectionHeader(
            'items',
            'Items / services',
            `${formData.lineItems.length} line item${formData.lineItems.length === 1 ? '' : 's'}`
          )}
          {openSection === 'items' ? (
            <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
              {formData.lineItems.map((item, index) => (
                <div
                  key={index}
                  className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-500">
                      Item {index + 1}
                    </span>
                    {formData.lineItems.length > 1 ? (
                      <button
                        type="button"
                        onClick={() => removeLineItem(index)}
                        className="text-xs font-semibold text-red-600 min-h-[36px] px-2"
                      >
                        Remove
                      </button>
                    ) : null}
                  </div>
                  <input
                    value={item.description}
                    onChange={(e) =>
                      handleLineItemChange(index, 'description', e.target.value)
                    }
                    placeholder="Description"
                    className="w-full min-h-[48px] px-3 rounded-xl border border-slate-300 bg-white text-base"
                  />
                  <div className="grid grid-cols-3 gap-2">
                    <label className="block">
                      <span className="text-[11px] text-slate-500">Qty</span>
                      <input
                        type="number"
                        inputMode="decimal"
                        value={item.qty}
                        onChange={(e) =>
                          handleLineItemChange(index, 'qty', e.target.value)
                        }
                        className="mt-1 w-full min-h-[44px] px-2 rounded-xl border border-slate-300 bg-white text-base"
                      />
                    </label>
                    <label className="block">
                      <span className="text-[11px] text-slate-500">Rate ($)</span>
                      <input
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        value={item.price}
                        onChange={(e) =>
                          handleLineItemChange(index, 'price', e.target.value)
                        }
                        className="mt-1 w-full min-h-[44px] px-2 rounded-xl border border-slate-300 bg-white text-base"
                      />
                    </label>
                    <label className="block">
                      <span className="text-[11px] text-slate-500">Amount</span>
                      <div className="mt-1 w-full min-h-[44px] px-2 rounded-xl border border-slate-200 bg-slate-100 flex items-center text-sm font-semibold text-slate-700">
                        {money(item.amount)}
                      </div>
                    </label>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={addLineItem}
                className="w-full min-h-[48px] rounded-xl border-2 border-dashed border-slate-300 text-sm font-semibold text-[#012E53]"
              >
                + Add item
              </button>
            </div>
          ) : null}
        </section>

        {/* Labor */}
        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          {sectionHeader(
            'labor',
            'Labor performed',
            formData.laborPerformed ? 'Work description set' : 'Describe work done'
          )}
          {openSection === 'labor' ? (
            <div className="px-4 pb-4 space-y-2 border-t border-slate-100 pt-3">
              <textarea
                value={formData.laborPerformed}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, laborPerformed: e.target.value }))
                }
                rows={4}
                placeholder="Describe the work performed..."
                className="w-full px-3 py-3 rounded-xl border border-slate-300 text-base"
              />
              <p className="text-xs text-slate-500 italic">{COMPANY.warranty}</p>
            </div>
          ) : null}
        </section>

        {/* Totals */}
        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          {sectionHeader('totals', 'Tax & discount', money(formData.totalAmount))}
          {openSection === 'totals' ? (
            <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Tax rate</span>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.0001"
                  value={formData.taxRate}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, taxRate: e.target.value }))
                  }
                  className="mt-1 w-full min-h-[48px] px-3 rounded-xl border border-slate-300 text-base"
                />
                <span className="text-[11px] text-slate-400">
                  Default 0.0825 (8.25% Texas)
                </span>
              </label>
              <div>
                <span className="text-xs font-medium text-slate-600">Discount</span>
                <div className="mt-1 grid grid-cols-2 gap-1 p-1 bg-slate-100 rounded-xl">
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((p) => ({ ...p, discountMode: 'amount' }))
                    }
                    className={`min-h-[44px] rounded-lg text-sm font-semibold ${
                      formData.discountMode === 'amount'
                        ? 'bg-white text-[#012E53] shadow-sm'
                        : 'text-slate-600'
                    }`}
                  >
                    Fixed ($)
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((p) => ({ ...p, discountMode: 'percent' }))
                    }
                    className={`min-h-[44px] rounded-lg text-sm font-semibold ${
                      formData.discountMode === 'percent'
                        ? 'bg-white text-[#012E53] shadow-sm'
                        : 'text-slate-600'
                    }`}
                  >
                    Percent (%)
                  </button>
                </div>
                {formData.discountMode === 'percent' ? (
                  <input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    value={formData.discountPercent}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, discountPercent: e.target.value }))
                    }
                    placeholder="0"
                    className="mt-2 w-full min-h-[48px] px-3 rounded-xl border border-slate-300 text-base"
                  />
                ) : (
                  <input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    value={formData.discount}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, discount: e.target.value }))
                    }
                    placeholder="0.00"
                    className="mt-2 w-full min-h-[48px] px-3 rounded-xl border border-slate-300 text-base"
                  />
                )}
              </div>
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Subtotal</span>
                  <span>{money(formData.subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">
                    Tax ({(Number(formData.taxRate) * 100).toFixed(2)}%)
                  </span>
                  <span>{money(formData.taxAmount)}</span>
                </div>
                {Number(formData.appliedDiscount) > 0 ? (
                  <div className="flex justify-between text-red-600">
                    <span>{discLabel}</span>
                    <span>-{money(formData.appliedDiscount)}</span>
                  </div>
                ) : null}
                <div className="flex justify-between font-bold text-base pt-2 border-t border-slate-200 text-[#012E53]">
                  <span>{formData.isEstimate ? 'Estimated total' : 'Balance due'}</span>
                  <span>{money(formData.totalAmount)}</span>
                </div>
              </div>
            </div>
          ) : null}
        </section>
      </main>

      {/* Sticky total + actions */}
      <div className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] safe-area-pb">
        <div className="max-w-lg mx-auto px-3 pt-2 pb-3">
          <div className="flex items-center justify-between mb-2 px-1">
            <span className="text-xs text-slate-500 uppercase tracking-wide font-medium">
              {formData.isEstimate ? 'Est. total' : 'Balance due'}
            </span>
            <span className="text-xl font-bold text-[#012E53]">
              {money(formData.totalAmount)}
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => setShowPreview(true)}
              className="min-h-[48px] rounded-xl border border-slate-300 text-sm font-semibold text-slate-700 bg-white"
            >
              Preview
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isLoading}
              className="min-h-[48px] rounded-xl text-white text-sm font-semibold disabled:opacity-50 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              style={{ background: INVOICE_CSS.primary }}
            >
              {isLoading ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowMore(true);
                setShareStatus('');
              }}
              className="min-h-[48px] rounded-xl border border-slate-300 text-sm font-semibold text-slate-700 bg-white"
            >
              More
            </button>
          </div>
        </div>
      </div>

      {/* Preview modal */}
      {showPreview ? (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4 pejman-sheet">
          <div className="pejman-sheet bg-white w-full max-w-lg max-h-[92dvh] overflow-y-auto rounded-t-2xl sm:rounded-2xl">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
              <h2 className="font-semibold text-slate-800">Formal preview</h2>
              <button
                type="button"
                onClick={() => setShowPreview(false)}
                className="min-h-[40px] px-3 text-sm font-semibold text-slate-600"
              >
                Close
              </button>
            </div>
            <div
              className="p-5 text-[13px] leading-normal"
              style={{
                color: INVOICE_CSS.foreground,
                background: '#FFFFFF',
                fontFeatureSettings: '"kern" 1',
                textRendering: 'optimizeLegibility'
              }}
            >
              <div className="flex justify-between items-start gap-3 pb-3 mb-1">
                <div className="min-w-0 flex-1">
                  <img
                    src={COMPANY.logoUi}
                    alt={COMPANY.name}
                    width={200}
                    height={80}
                    className="h-14 w-auto max-w-[200px] object-contain object-left"
                  />
                </div>
                <div className="text-right shrink-0 max-w-[55%]">
                  <div
                    className="font-semibold text-[12px] leading-tight"
                    style={{ color: INVOICE_CSS.primary }}
                  >
                    {COMPANY.name}
                  </div>
                  <div className="text-[11px] mt-1 leading-relaxed" style={{ color: INVOICE_CSS.muted }}>
                    {COMPANY.address}
                    <br />
                    {COMPANY.phone}
                    {' · '}
                    {COMPANY.email}
                  </div>
                  <div
                    className="text-xl font-bold mt-3 tracking-[0.06em]"
                    style={{ color: INVOICE_CSS.primary }}
                  >
                    {formData.isEstimate ? 'ESTIMATE' : 'INVOICE'}
                  </div>
                  {formData.isEstimate ? (
                    <div
                      className="text-xs font-semibold mt-1"
                      style={{ color: INVOICE_CSS.estimate }}
                    >
                      Valid for 30 days
                    </div>
                  ) : null}
                </div>
              </div>
              <div
                className="mb-4"
                style={{
                  borderTop: `2px solid ${INVOICE_CSS.primary}`,
                  boxShadow: `0 1.5px 0 0 ${INVOICE_CSS.brandRed}`
                }}
              />
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <div
                    className="text-[10px] font-bold tracking-wider mb-1"
                    style={{ color: INVOICE_CSS.primary }}
                  >
                    BILL TO
                  </div>
                  <div className="font-semibold">{formData.clientName || '—'}</div>
                  <div className="text-xs whitespace-pre-line" style={{ color: INVOICE_CSS.muted }}>
                    {formData.clientAddress || '—'}
                  </div>
                  <div className="text-xs mt-1" style={{ color: INVOICE_CSS.muted }}>
                    {formData.clientPhone || ''}
                  </div>
                  {formData.clientModel ? (
                    <div className="text-xs" style={{ color: INVOICE_CSS.muted }}>
                      Equip: {formData.clientModel}
                    </div>
                  ) : null}
                </div>
                <div className="text-right text-xs space-y-1">
                  <div>
                    <span style={{ color: INVOICE_CSS.muted }}>
                      {formData.isEstimate ? 'Estimate #' : 'Invoice #'}
                    </span>
                    <br />
                    <span className="font-semibold">{formData.invoiceNumber}</span>
                  </div>
                  <div>
                    <span style={{ color: INVOICE_CSS.muted }}>Date</span>
                    <br />
                    <span className="font-semibold">
                      {new Date(formData.serviceDate).toLocaleDateString('en-US')}
                    </span>
                  </div>
                  <div>
                    <span style={{ color: INVOICE_CSS.muted }}>PO Number</span>
                    <br />
                    <span className="font-semibold">{formData.poNumber || '—'}</span>
                  </div>
                </div>
              </div>
              <table className="w-full text-xs mb-4">
                <thead>
                  <tr style={{ background: INVOICE_CSS.primary, color: '#FFFFFF' }}>
                    <th className="text-left py-2.5 px-2">Qty</th>
                    <th className="text-left py-2.5 px-2">Description</th>
                    <th className="text-right py-2.5 px-2">Rate</th>
                    <th className="text-right py-2.5 px-2">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.lineItems.map((item, i) => (
                    <tr
                      key={i}
                      style={{
                        background: i % 2 ? INVOICE_CSS.background : '#FFFFFF',
                        borderBottom: `1px solid ${INVOICE_CSS.border}`
                      }}
                    >
                      <td className="py-2 px-2">{item.qty}</td>
                      <td className="py-2 px-2">{item.description}</td>
                      <td className="py-2 px-2 text-right">{money(item.price)}</td>
                      <td className="py-2 px-2 text-right">{money(item.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {formData.laborPerformed ? (
                <div className="mb-3">
                  <div
                    className="text-[10px] font-bold tracking-wider mb-1"
                    style={{ color: INVOICE_CSS.primary }}
                  >
                    LABOR / WORK PERFORMED
                  </div>
                  <p className="text-xs whitespace-pre-wrap" style={{ color: INVOICE_CSS.muted }}>
                    {formData.laborPerformed}
                  </p>
                </div>
              ) : null}
              <p className="text-[11px] italic mb-3" style={{ color: INVOICE_CSS.muted }}>
                {COMPANY.warranty}
              </p>
              <div className="ml-auto w-48 space-y-1 text-xs">
                <div className="flex justify-between">
                  <span style={{ color: INVOICE_CSS.muted }}>Subtotal</span>
                  <span>{money(formData.subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: INVOICE_CSS.muted }}>Tax</span>
                  <span>{money(formData.taxAmount)}</span>
                </div>
                {Number(formData.appliedDiscount) > 0 ? (
                  <div className="flex justify-between" style={{ color: INVOICE_CSS.destructive }}>
                    <span>{discLabel}</span>
                    <span>-{money(formData.appliedDiscount)}</span>
                  </div>
                ) : null}
                <div
                  className="flex justify-between font-bold text-sm pt-2"
                  style={{
                    borderTop: `2px solid ${INVOICE_CSS.primary}`,
                    color: INVOICE_CSS.accent
                  }}
                >
                  <span style={{ color: INVOICE_CSS.primary }}>
                    {formData.isEstimate ? 'Estimated total' : 'Balance due'}
                  </span>
                  <span>{money(formData.totalAmount)}</span>
                </div>
              </div>
              {!formData.isEstimate && Number(formData.totalAmount) > 0 ? (
                <div
                  className="mt-4 rounded-xl p-3 space-y-2"
                  style={{
                    background: INVOICE_CSS.background,
                    border: `1px solid ${INVOICE_CSS.border}`
                  }}
                >
                  <div
                    className="text-[10px] font-bold tracking-[0.08em] uppercase"
                    style={{ color: INVOICE_CSS.primary }}
                  >
                    Pay with Venmo
                  </div>
                  <p className="text-xs" style={{ color: INVOICE_CSS.muted }}>
                    @{COMPANY.venmoUsername} · {money(formData.totalAmount)}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={openVenmoPay}
                      className="min-h-[44px] rounded-xl text-white text-sm font-semibold cursor-pointer"
                      style={{ background: '#008CFF' }}
                    >
                      Open Venmo
                    </button>
                    <button
                      type="button"
                      onClick={shareVenmoLink}
                      className="min-h-[44px] rounded-xl border text-sm font-semibold cursor-pointer"
                      style={{ borderColor: INVOICE_CSS.border, color: INVOICE_CSS.primary }}
                    >
                      Send pay link
                    </button>
                  </div>
                </div>
              ) : null}
              <div
                className="mt-5 pt-3 text-[11px] space-y-2"
                style={{ borderTop: `1px solid ${INVOICE_CSS.border}`, color: INVOICE_CSS.muted }}
              >
                <p>
                  <strong style={{ color: INVOICE_CSS.foreground }}>Terms:</strong>{' '}
                  {formData.terms}
                </p>
                <p className="text-right italic" style={{ color: INVOICE_CSS.primary }}>
                  Authorized By: {COMPANY.authorizedBy}
                </p>
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-slate-200 p-3 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setShowPreview(false)}
                className="min-h-[48px] rounded-xl border border-slate-300 font-semibold text-sm"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowPreview(false);
                  handleSave();
                }}
                className="min-h-[48px] rounded-xl text-white font-semibold text-sm cursor-pointer"
                style={{ background: INVOICE_CSS.primary }}
              >
                Confirm &amp; Save
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* More sheet */}
      {showMore ? (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end justify-center pejman-sheet">
          <div className="pejman-sheet bg-white w-full max-w-lg rounded-t-2xl p-4 pb-[max(2rem,env(safe-area-inset-bottom))] space-y-2 max-h-[85dvh] overflow-y-auto">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-slate-800">More actions</h3>
              <button
                type="button"
                onClick={() => setShowMore(false)}
                className="min-h-[40px] px-3 text-sm font-semibold text-slate-600"
              >
                Close
              </button>
            </div>
            {shareStatus ? (
              <p className="text-sm text-slate-600 bg-slate-50 rounded-xl px-3 py-2">
                {shareStatus}
              </p>
            ) : null}
            {!formData.isEstimate ? (
              <div
                className="rounded-xl p-3 space-y-2 mb-1"
                style={{
                  background: '#E8F4FF',
                  border: '1px solid #B6DBFF'
                }}
              >
                <div className="text-sm font-semibold" style={{ color: '#008CFF' }}>
                  Venmo payment
                </div>
                <p className="text-xs text-slate-600">
                  @{COMPANY.venmoUsername} · amount {money(formData.totalAmount)} prefilled
                </p>
                <button
                  type="button"
                  disabled={shareLoading}
                  onClick={openVenmoPay}
                  className="w-full min-h-[48px] rounded-xl text-white text-sm font-semibold disabled:opacity-50"
                  style={{ background: '#008CFF' }}
                >
                  Open Venmo pay
                </button>
                <button
                  type="button"
                  disabled={shareLoading}
                  onClick={shareVenmoLink}
                  className="w-full min-h-[48px] rounded-xl border border-sky-200 bg-white text-sm font-semibold text-slate-800 disabled:opacity-50"
                >
                  Share payment link to customer
                </button>
                <button
                  type="button"
                  disabled={shareLoading}
                  onClick={copyVenmoLink}
                  className="w-full min-h-[44px] rounded-xl text-sm font-semibold text-sky-800 disabled:opacity-50"
                >
                  Copy Venmo message
                </button>
              </div>
            ) : null}
            {[
              { label: 'Download PDF', action: downloadPDF },
              { label: 'Share (native)', action: shareNative },
              { label: 'Email to client', action: sendToClient },
              { label: 'Copy summary', action: copySummary },
              { label: 'Copy last invoice', action: copyLastInvoice }
            ].map((item) => (
              <button
                key={item.label}
                type="button"
                disabled={shareLoading}
                onClick={item.action}
                className="w-full min-h-[48px] rounded-xl border border-slate-200 text-left px-4 text-sm font-semibold text-slate-800 active:bg-slate-50 disabled:opacity-50"
              >
                {item.label}
              </button>
            ))}
            <div className="rounded-xl border border-slate-200 p-3 space-y-2">
              <div className="text-xs font-semibold text-slate-600">
                Convert estimate → invoice
              </div>
              <div className="flex gap-2">
                <input
                  value={convertEstNum}
                  onChange={(e) => setConvertEstNum(e.target.value)}
                  placeholder="EST-…"
                  className="flex-1 min-h-[44px] px-3 rounded-xl border border-slate-300 text-sm"
                />
                <button
                  type="button"
                  onClick={convertEstimateToInvoice}
                  className="min-h-[44px] px-4 rounded-xl bg-slate-800 text-white text-sm font-semibold"
                >
                  Convert
                </button>
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 p-3">
              <button
                type="button"
                onClick={() => setEditTerms((v) => !v)}
                className="text-sm font-semibold text-slate-700 min-h-[40px]"
              >
                {editTerms ? 'Hide terms' : 'Edit payment terms'}
              </button>
              {editTerms ? (
                <textarea
                  value={formData.terms}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, terms: e.target.value }))
                  }
                  rows={4}
                  className="mt-2 w-full px-3 py-2 rounded-xl border border-slate-300 text-sm"
                />
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PejmanPage;
